import { API_BASE_URL } from '../config';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('ApiClient');
import { ApiError, ApiRequestConfig, ApiResponse } from './types';

// Cache interface
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

// Error handler type
type ErrorHandler = (error: ApiError) => void;

// Request/Response interceptor types
type RequestInterceptor = (config: RequestInit, url: string) => Promise<RequestInit> | RequestInit;
type ResponseInterceptor = (response: Response, url: string) => Promise<Response> | Response;

class ApiClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private cache = new Map<string, CacheEntry<unknown>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes default
  private errorHandlers: ErrorHandler[] = [];
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL.replace(/\/$/, ''); // Remove trailing slash
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    // Set up default global error handler
    this.addErrorHandler((error: ApiError) => {
      // Only show user-facing errors for 4xx and 5xx errors
      if (error.status >= 400) {
        logger.error('API Error:', error);
        
        // Check if we're in a browser environment before showing notifications
        if (typeof window !== 'undefined') {
          this.showErrorNotification(error);
        }
      }
    });
  }

  /**
   * Show error notification to user
   */
  private showErrorNotification(error: ApiError): void {
    // Import Ant Design message dynamically to avoid SSR issues
    import('antd').then(({ message }) => {
      let errorMessage = error.message;
      
      // Provide user-friendly messages for common errors
      switch (error.status) {
        case 400:
          errorMessage = 'Invalid request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Authentication required. Please log in.';
          break;
        case 403:
          errorMessage = 'Access denied. You do not have permission for this action.';
          break;
        case 404:
          errorMessage = 'Resource not found.';
          break;
        case 429:
          errorMessage = 'Too many requests. Please try again later.';
          break;
        case 500:
          errorMessage = 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service unavailable. Please try again later.';
          break;
        default:
          if (error.status >= 500) {
            errorMessage = 'Server error. Please try again later.';
          }
      }

      message.error(errorMessage);
    }).catch((err) => {
      // Fallback if antd is not available
      logger.error('Failed to show error notification:', err);
      logger.error('Original error:', error);
    });
  }

  /**
   * Add global error handler
   */
  addErrorHandler(handler: ErrorHandler): void {
    this.errorHandlers.push(handler);
  }

  /**
   * Remove error handler
   */
  removeErrorHandler(handler: ErrorHandler): void {
    const index = this.errorHandlers.indexOf(handler);
    if (index > -1) {
      this.errorHandlers.splice(index, 1);
    }
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  /**
   * Call all error handlers
   */
  private handleError(error: ApiError): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (err) {
        logger.error('Error handler failed:', err);
      }
    });
  }

  /**
   * Set default headers for all requests
   */
  setDefaultHeaders(headers: Record<string, string>): void {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * Set authorization header
   */
  setAuthToken(token: string): void {
    this.defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization header
   */
  clearAuthToken(): void {
    delete this.defaultHeaders['Authorization'];
  }

  /**
   * Build full URL with query parameters
   */
  private buildUrl(endpoint: string, params?: Record<string, unknown>): string {
    const url = `${this.baseURL}${endpoint}`;
    
    if (!params || Object.keys(params).length === 0) {
      return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    return `${url}?${searchParams.toString()}`;
  }

  /**
   * Generate cache key for request
   */
  private getCacheKey(method: string, url: string, body?: unknown): string {
    const bodyStr = body ? JSON.stringify(body) : '';
    return `${method}:${url}:${bodyStr}`;
  }

  /**
   * Get cached response if available and not expired
   */
  private getCachedResponse<T>(cacheKey: string): T | null {
    const entry = this.cache.get(cacheKey);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(cacheKey);
      return null;
    }

    logger.debug('Using cached response for:', cacheKey);
    return entry.data;
  }

  /**
   * Cache response
   */
  private setCachedResponse<T>(cacheKey: string, data: T, ttl: number = this.CACHE_TTL): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };
    this.cache.set(cacheKey, entry);
  }

  /**
   * Clear all cached responses
   */
  clearCache(): void {
    this.cache.clear();
    logger.debug('API cache cleared');
  }

  /**
   * Clear specific cache entries by pattern
   */
  clearCachePattern(pattern: string): void {
    const regex = new RegExp(pattern);
    for (const [key] of this.cache) {
      if (regex.test(key)) {
        this.cache.delete(key);
      }
    }
    logger.debug('Cache cleared for pattern:', pattern);
  }

  /**
   * Clear a specific cache entry by exact key
   */
  clearCacheKey(cacheKey: string): boolean {
    const isDevelopment: boolean = process.env.NODE_ENV === 'development';
    
    const deleted = this.cache.delete(cacheKey);
    
    if (deleted) {
      logger.debug('✅ Cache entry deleted:', cacheKey);
    } else {
      logger.debug('ℹ️ Cache entry not found:', cacheKey);
    }
    
    // Print entire cache content in development
    if (isDevelopment) {
      const allCacheKeys = Array.from(this.cache.keys());
      logger.debug('🔍 Current cache state:', {
        totalEntries: allCacheKeys.length,
        keys: allCacheKeys
      });
    }
    
    return deleted;
  }

  /**
   * Clear cache for a specific pipeline/workflow file
   * This is a convenience method for clearing cache after saves
   */
  clearPipelineCache(path: string, type: 'pipeline' | 'workflow' = 'pipeline'): boolean {
    const endpoint = type === 'workflow' ? '/v1/workflow/getWorkflow' : '/v1/pipeline/getPipeline';
    
    // Build the URL with query params (same as buildUrl does)
    const url = `${this.baseURL}${endpoint}?path=${encodeURIComponent(path)}`;
    
    // Build the cache key with empty body object (same as GET requests have)
    // GET requests pass an empty config object which becomes {} in the cache key
    const cacheKey = this.getCacheKey('GET', url, {});
    
    // Use the clearCacheKey function for consistency
    return this.clearCacheKey(cacheKey);
  }

  /**
   * Clear cache for file list
   * This is used when refreshing the file browser
   */
  clearFileListCache(): boolean {
    const endpoint = '/v1/file/getFileList';
    
    // Build the URL (no query params for file list)
    const url = `${this.baseURL}${endpoint}`;
    
    // Build the cache key with empty body object (same as GET requests have)
    const cacheKey = this.getCacheKey('GET', url, {});
    
    // Use the clearCacheKey function for consistency
    return this.clearCacheKey(cacheKey);
  }

  /**
   * Sleep function for retry delays
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Check if error is retryable
   */
  private isRetryableError(status: number): boolean {
    return status >= 500 || status === 408 || status === 429;
  }

  /**
   * Make HTTP request with retry logic
   */
  private async makeRequest<T>(
    method: string,
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<ApiResponse<T>> {
    const {
      headers = {},
      params,
      timeout = 30000,
      retries = 3,
      cache = method === 'GET',
      signal,
      ...body
    } = config;

    const url = this.buildUrl(endpoint, params);
    const cacheKey = this.getCacheKey(method, url, body);

    // Check cache for GET requests
    if (cache && method === 'GET') {
      const cachedResponse = this.getCachedResponse<ApiResponse<T>>(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }
    }

    let requestHeaders = {
      ...this.defaultHeaders,
      ...headers,
    };

    let requestConfig: RequestInit = {
      method,
      headers: requestHeaders,
      signal,
    };

    // Add body for non-GET requests
    if (method !== 'GET' && Object.keys(body).length > 0) {
      const bodyString = JSON.stringify(body);
      requestConfig.body = bodyString;
      
      // Log request body for debugging
      logger.debug('HTTP Request Body:', {
        method,
        url,
        bodyKeys: Object.keys(body),
        bodyString,
        bodyObject: body
      });
    }

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      try {
        requestConfig = await Promise.resolve(interceptor(requestConfig, url));
      } catch (error) {
        logger.error('Request interceptor failed:', error);
      }
    }

    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // Create timeout controller
        const timeoutController = new AbortController();
        const timeoutId = setTimeout(() => timeoutController.abort(), timeout);

        // Combine timeout and user-provided signal
        const combinedSignal = signal 
          ? AbortSignal.any([signal, timeoutController.signal])
          : timeoutController.signal;

        let response = await fetch(url, {
          ...requestConfig,
          signal: combinedSignal,
        });

        clearTimeout(timeoutId);

        // Apply response interceptors
        for (const interceptor of this.responseInterceptors) {
          try {
            response = await Promise.resolve(interceptor(response, url));
          } catch (error) {
            logger.error('Response interceptor failed:', error);
          }
        }

        const responseData = await this.handleResponse<T>(response);

        // Cache successful GET responses
        if (cache && method === 'GET' && response.ok) {
          this.setCachedResponse(cacheKey, responseData);
        }

        return responseData;

      } catch (error) {
        lastError = error as Error;
        
        // Don't retry on abort or non-retryable errors
        if (error instanceof Error && error.name === 'AbortError') {
          const apiError = new ApiError('Request was aborted', 0);
          this.handleError(apiError);
          throw apiError;
        }

        // Don't retry on ApiError with non-retryable status (like 400 Bad Request)
        if (error instanceof ApiError) {
          if (!this.isRetryableError(error.status)) {
            this.handleError(error);
            throw error;
          }
        }

        // For fetch errors (network errors), we don't have a status code, so we can retry
        if (attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000); // Exponential backoff, max 10s
          logger.warn(`Request attempt ${attempt + 1} failed, retrying in ${delay}ms:`, error);
          await this.sleep(delay);
          continue;
        }

        // If it's a network error or timeout, throw a custom error
        if (error instanceof TypeError || (error as Error).message?.includes('fetch')) {
          const apiError = new ApiError('Network error or server unavailable', 0);
          this.handleError(apiError);
          throw apiError;
        }

        // Handle other errors
        if (error instanceof ApiError) {
          this.handleError(error);
        }

        throw error;
      }
    }

    const finalError = lastError || new ApiError('Request failed after all retries', 0);
    if (finalError instanceof ApiError) {
      this.handleError(finalError);
    }
    throw finalError;
  }

  /**
   * Handle response and extract data
   */
  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    const contentType = response.headers.get('content-type');
    
    let data: unknown;
    try {
      if (contentType?.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }
    } catch (error) {
      throw new ApiError('Failed to parse response', response.status);
    }

    const apiResponse: ApiResponse<T> = {
      data,
      status: response.status,
    };

    if (!response.ok) {
      // For 400 Bad Request, the response body contains ValidationResultDTO
      // Store the entire response data in details so it can be extracted by the caller
      const error = new ApiError(
        data?.message || data?.error || response.statusText || 'Unknown error',
        response.status,
        data?.code,
        response.status === 400 ? data : data?.details // For 400, store full data as details
      );

      logger.error('API Error:', {
        status: response.status,
        message: error.message,
        data: data,
        details: error.details
      });
      throw error;
    }

    return apiResponse;
  }

  /**
   * GET request
   */
  async get<T = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, 'method'>): Promise<T> {
    const response = await this.makeRequest<T>('GET', endpoint, config);
    return response.data as T;
  }

  /**
   * POST request
   */
  async post<T = unknown>(
    endpoint: string, 
    data?: unknown, 
    config?: Omit<ApiRequestConfig, 'method'>
  ): Promise<T> {
    const response = await this.makeRequest<T>('POST', endpoint, { ...data, ...config });
    return response.data as T;
  }

  /**
   * PUT request
   */
  async put<T = unknown>(
    endpoint: string, 
    data?: unknown, 
    config?: Omit<ApiRequestConfig, 'method'>
  ): Promise<T> {
    const response = await this.makeRequest<T>('PUT', endpoint, { ...data, ...config });
    return response.data as T;
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(endpoint: string, config?: Omit<ApiRequestConfig, 'method'>): Promise<T> {
    const response = await this.makeRequest<T>('DELETE', endpoint, config);
    return response.data as T;
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(
    endpoint: string, 
    data?: unknown, 
    config?: Omit<ApiRequestConfig, 'method'>
  ): Promise<T> {
    const response = await this.makeRequest<T>('PATCH', endpoint, { ...data, ...config });
    return response.data as T;
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export ApiError for external use
export { ApiError };

// Export the class for testing or multiple instances
export default ApiClient; 